/**
 * usePrivacyStore — Sunum/gizlilik modu.
 *
 * Şub 16, 2026 — Müşteri sunumları sırasında ekrandaki tüm para tutarlarını
 * blur'layan global toggle. `<Money>` ve `<SensitiveValue>` wrapper'ları bu
 * store'u dinler. Tercih localStorage'da kalıcı; sekmeler arası senkron
 * BroadcastChannel ile sağlanır (login ekranındaki açıp dashboard'a geçince
 * de açık kalır).
 *
 * State:
 *   privacyMode: boolean — true ise tüm `<Money>` blurlanır.
 *   revealedKeys: Set<string> — kullanıcının manuel olarak gösterdiği alanlar
 *     (tek bir satırı görmek için). Toggle kapanınca otomatik temizlenir.
 *
 * Klavye kısayolu: Ctrl+Shift+H (Hide).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const CHANNEL_NAME = 'privacy-mode-sync';
let bc = null;
let bcInitialized = false;

function ensureChannel(onMessage) {
  if (bcInitialized) return bc;
  bcInitialized = true;
  try {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (ev) => {
        if (ev?.data?.type === 'set-privacy' && typeof ev.data.value === 'boolean') {
          onMessage(ev.data.value);
        }
      };
    }
  } catch {
    bc = null;
  }
  return bc;
}

export const usePrivacyStore = create(
  persist(
    (set, get) => ({
      privacyMode: false,
      // Set'i serialize edemediğimiz için array tutuyoruz; selector tarafı
      // .includes() ile O(n) çalışır (uçta < 20 reveal'lı senaryo bekleniyor).
      revealedKeys: [],

      setPrivacyMode: (value, opts = {}) => {
        const v = !!value;
        set({ privacyMode: v, revealedKeys: [] });
        // Diğer sekmelere bildir (kendimize bildirim atmayalım diye opts.skipBroadcast)
        if (!opts.skipBroadcast) {
          const channel = ensureChannel((remoteValue) => {
            // Uzaktan gelen değişikliği state'e uygula (skipBroadcast ile loop'u kır)
            get().setPrivacyMode(remoteValue, { skipBroadcast: true });
          });
          try { channel?.postMessage({ type: 'set-privacy', value: v }); } catch { /* ignore */ }
        }
      },

      togglePrivacyMode: () => {
        get().setPrivacyMode(!get().privacyMode);
      },

      revealKey: (key) => {
        if (!key) return;
        const cur = get().revealedKeys;
        if (cur.includes(key)) return;
        set({ revealedKeys: [...cur, key] });
      },

      hideKey: (key) => {
        if (!key) return;
        set({ revealedKeys: get().revealedKeys.filter((k) => k !== key) });
      },

      isRevealed: (key) => get().revealedKeys.includes(key),
    }),
    {
      name: 'oms-privacy-mode',
      partialize: (state) => ({ privacyMode: state.privacyMode }),
    },
  ),
);

// İlk yüklenmede channel'ı kur (uzak sekmelerden gelen değişiklikleri yakalamak için)
if (typeof window !== 'undefined') {
  ensureChannel((remoteValue) => {
    const cur = usePrivacyStore.getState();
    if (cur.privacyMode !== remoteValue) {
      cur.setPrivacyMode(remoteValue, { skipBroadcast: true });
    }
  });

  // Global klavye kısayolu: Ctrl+Shift+H
  window.addEventListener('keydown', (ev) => {
    if (ev.ctrlKey && ev.shiftKey && (ev.key === 'H' || ev.key === 'h')) {
      ev.preventDefault();
      usePrivacyStore.getState().togglePrivacyMode();
    }
  });
}
